import { useEffect, useRef, useState } from 'react';
import { Input } from "@/components/ui/input";
import { FormControl } from "@/components/ui/form";
import { Loader2, Search, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface PlacesAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult & {
    latitude?: number;
    longitude?: number;
  }) => void;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
}

interface Prediction {
  place_id: string;
  description: string;
}

export function PlacesAutocomplete({
  onPlaceSelect,
  value,
  onChange,
  placeholder = "Enter a location",
  className
}: PlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [searchQuery, setSearchQuery] = useState(value || "");
  const [isSearching, setIsSearching] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [noResults, setNoResults] = useState(false);

  useEffect(() => {
    // Check if the script is already loaded
    if (window.google?.maps) {
      setScriptLoaded(true);
      return;
    }

    setIsLoading(true);
    
    // Debug log
    console.log('Loading Google Maps with key:', 'AIzaSyD2YL9rBJo_fjtFgVXIS8HRR5DSx_K27Vw');
    
    // Load the Google Maps JavaScript API
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCDc-wyoI09NynFrLCREpUj7dJG2oGFkz4&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.addEventListener('load', () => {
      setScriptLoaded(true);
      setIsLoading(false);
    });

    script.addEventListener('error', (error) => {
      console.error('Error loading Google Maps script:', error);
      setIsLoading(false);
    });

    document.body.appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      document.body.removeChild(script);
    };
  }, []);

  // Update internal state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSearchQuery(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (open && !document.getElementById('places-autocomplete-container')?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleSearch = () => {
    if (!scriptLoaded || !searchQuery.trim()) return;
    
    setIsSearching(true);
    setPredictions([]);
    setNoResults(false);

    // Clean up previous autocomplete instance if it exists
    if (autocompleteRef.current) {
      google.maps.event.clearInstanceListeners(autocompleteRef.current);
      autocompleteRef.current = null;
    }

    // Initialize Google Maps Autocomplete with the current search query
    const autocompleteService = new google.maps.places.AutocompleteService();
    
    autocompleteService.getPlacePredictions(
      {
        input: searchQuery,
        types: ['geocode', 'establishment']
      },
      (results, status) => {
        setIsSearching(false);
        
        if (status !== google.maps.places.PlacesServiceStatus.OK || !results || results.length === 0) {
          setNoResults(true);
          setOpen(true);
          return;
        }
        
        // Store predictions for display
        setPredictions(results.map(prediction => ({
          place_id: prediction.place_id,
          description: prediction.description
        })));
        
        // Open the dropdown
        setOpen(true);
      }
    );
  };

  const handleSelectPlace = (placeId: string) => {
    if (!scriptLoaded) return;
    
    setIsSearching(true);
    setOpen(false);
    
    const placesService = new google.maps.places.PlacesService(document.createElement('div'));
    
    placesService.getDetails(
      {
        placeId: placeId,
        fields: ['place_id', 'geometry', 'name', 'formatted_address']
      },
      (place, detailsStatus) => {
        setIsSearching(false);
        
        if (detailsStatus !== google.maps.places.PlacesServiceStatus.OK || !place) {
          return;
        }
        
        // Extract coordinates if available
        const latitude = place.geometry?.location?.lat();
        const longitude = place.geometry?.location?.lng();
        
        console.log('Place selected:', { 
          address: place.formatted_address,
          coordinates: { lat: latitude, lng: longitude }
        });

        // Add coordinates to the place object
        const placeWithCoordinates = {
          ...place,
          latitude: typeof latitude === 'number' ? latitude : undefined,
          longitude: typeof longitude === 'number' ? longitude : undefined
        };
        
        onPlaceSelect(placeWithCoordinates);

        // Update the input value with the formatted address
        if (onChange && place.formatted_address) {
          onChange(place.formatted_address);
          setSearchQuery(place.formatted_address);
        }
      }
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    // Only update parent component if onChange is provided
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div id="places-autocomplete-container" className="relative w-full">
      <div className="flex w-full">
        <FormControl className="flex-grow">
          <Input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={className}
            disabled={isLoading || isSearching}
          />
        </FormControl>
        <Button 
          type="button"
          variant="outline"
          size="icon"
          className="ml-2"
          onClick={handleSearch}
          disabled={isLoading || isSearching || !searchQuery.trim()}
        >
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </Button>
      </div>
      
      {/* Dropdown for predictions */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-md max-h-[300px] overflow-y-auto">
          {predictions.length > 0 ? (
            <Command>
              <CommandList>
                <CommandGroup>
                  {predictions.map((prediction) => (
                    <CommandItem
                      key={prediction.place_id}
                      onSelect={() => handleSelectPlace(prediction.place_id)}
                      className="flex items-center gap-2 py-2"
                    >
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{prediction.description}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          ) : (
            <div className="p-4 text-center text-sm text-muted-foreground">
              {isSearching ? 'Searching...' : 'No results found'}
            </div>
          )}
        </div>
      )}
      
      {isLoading && (
        <div className="absolute right-12 top-1/2 -translate-y-1/2">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
    </div>
  );
} 